import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { getShiftSummary, askAssistant } from '../../../../services';
import { formatPowerAutoUnit } from '../../../../utils';
import * as s from './styled-shift-summary';

// Resumo de turno gerado por IA: briefing em PT-BR da janela recente
// (paradas, causas recorrentes, geracao, atencao pro proximo turno) +
// stats estruturados. O LLM roda no BACKEND (com cache ~10min) — aqui so
// exibimos. Quando summary vem null (LLM indisponivel), degrada pros
// stats puros sem quebrar o fluxo do operador.

const WINDOWS = [
  { hours: 8, label: '8h' },
  { hours: 12, label: '12h' },
  { hours: 24, label: '24h' },
];

// Aceita epoch (s ou ms) E ISO string — o backend manda ISO.
const fmtSince = (ts) => {
  if (!ts) return '';
  const n = Number(ts);
  const t = Number.isFinite(n) ? (n > 1e12 ? n : n * 1000) : Date.parse(ts);
  if (!Number.isFinite(t)) return '';
  const ms = Date.now() - t;
  if (ms < 0) return 'agora';
  const min = Math.floor(ms / 60000);
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  return `há ${Math.floor(h / 24)}d`;
};

// initialQuestion (opcional): pergunta pre-montada (ex.: triagem de um
// alarme clicado no painel) enviada automaticamente ao abrir o modal.
const ShiftSummaryModal = ({ isOpen, onClose, initialQuestion }) => {
  const [hours, setHours] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  // Copiloto: conversa livre sobre a operacao (tool-use no backend, somente
  // dados do escopo do usuario). chat = [{role, content}].
  const [chat, setChat] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [asking, setAsking] = useState(false);
  const chatEndRef = useRef(null);

  // Auto-scroll pro fim da conversa quando chega resposta.
  useEffect(() => {
    if (chatEndRef.current && chatEndRef.current.scrollIntoView) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [chat, asking]);

  const ask = useCallback((question) => {
    const q = (question || '').trim();
    if (!q || asking) return;
    setAsking(true);
    setChat((prev) => [...prev, { role: 'user', content: q }]);
    // history = ultimos 6 turnos ANTES da pergunta atual.
    const history = chat.slice(-6);
    askAssistant(q, history)
      .then((res) => {
        let body = (res && res.data) || res || {};
        if (body.data && body.data.answer !== undefined) body = body.data;
        const answer =
          body.answer ||
          'Não consegui responder agora — tente novamente em instantes.';
        setChat((prev) => [...prev, { role: 'assistant', content: answer }]);
      })
      .catch(() => {
        setChat((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: 'Erro ao consultar o copiloto. Tente novamente.',
          },
        ]);
      })
      .finally(() => setAsking(false));
  }, [asking, chat]);

  const handleAsk = useCallback(() => {
    const q = chatInput.trim();
    if (!q) return;
    setChatInput('');
    ask(q);
  }, [chatInput, ask]);

  // Pergunta inicial (triagem de alarme): envia 1x ao abrir com
  // initialQuestion. Ref guarda contra re-envio em re-renders.
  const initialAskedRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      initialAskedRef.current = false;
      return;
    }
    if (initialQuestion && !initialAskedRef.current) {
      initialAskedRef.current = true;
      ask(initialQuestion);
    }
  }, [isOpen, initialQuestion, ask]);

  const load = useCallback((h) => {
    setLoading(true);
    setError(false);
    getShiftSummary(h)
      .then((res) => {
        // Tolerante ao envelope: { data: {...} } ou { data: { data: {...} } }.
        let body = (res && res.data) || res || null;
        if (body && body.data && (body.data.stats || body.data.summary !== undefined)) {
          body = body.data;
        }
        setData(body && body.stats ? body : null);
        setError(!(body && body.stats));
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (isOpen) load(hours);
  }, [isOpen, hours, load]);

  // ESC fecha.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const stats = (data && data.stats) || null;

  return ReactDOM.createPortal(
    <s.Backdrop
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <s.Panel role="dialog" aria-modal="true" aria-label="Resumo do turno">
        <s.Header>
          <s.Title>
            {/* lucide:sparkles */}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 3l1.9 5.8a2 2 0 0 0 1.3 1.3L21 12l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 21l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 12l5.8-1.9a2 2 0 0 0 1.3-1.3L12 3z" />
            </svg>
            Resumo do turno
            <s.AiTag>IA</s.AiTag>
          </s.Title>
          <s.HeaderRight>
            <s.WindowToggle>
              {WINDOWS.map((w) => (
                <s.WindowBtn
                  key={w.hours}
                  type="button"
                  $active={hours === w.hours}
                  onClick={() => setHours(w.hours)}
                >
                  {w.label}
                </s.WindowBtn>
              ))}
            </s.WindowToggle>
            <s.Close type="button" onClick={onClose} aria-label="Fechar">
              ×
            </s.Close>
          </s.HeaderRight>
        </s.Header>

        <s.Body>
          {loading ? (
            <s.Loading>Gerando resumo…</s.Loading>
          ) : error ? (
            <s.Empty>Não foi possível carregar o resumo do turno.</s.Empty>
          ) : (
            <>
              {data && data.summary ? (
                <s.Summary>{data.summary}</s.Summary>
              ) : (
                <s.Empty>
                  Resumo de IA indisponível no momento — veja os indicadores
                  do período abaixo.
                </s.Empty>
              )}

              {stats && (
                <>
                  <s.ChipsRow>
                    <s.Chip $tone={stats.alarmCount > 0 ? 'red' : 'ok'}>
                      {stats.alarmCount} alarme{stats.alarmCount === 1 ? '' : 's'}
                    </s.Chip>
                    <s.Chip $tone={stats.blockCount > 0 ? 'red' : 'ok'}>
                      {stats.blockCount} bloqueio{stats.blockCount === 1 ? '' : 's'}
                    </s.Chip>
                    <s.Chip $tone={stats.tripCount > 0 ? 'red' : 'ok'}>
                      {stats.tripCount} trip{stats.tripCount === 1 ? '' : 's'}
                    </s.Chip>
                    <s.Chip>
                      {formatPowerAutoUnit(stats.generationNowKw || 0, 'kW')}{' '}
                      gerando agora
                    </s.Chip>
                  </s.ChipsRow>

                  {stats.stoppedNow && stats.stoppedNow.length > 0 && (
                    <s.Section>
                      <s.SectionLabel>Paradas agora</s.SectionLabel>
                      {stats.stoppedNow.map((m) => (
                        <s.Row key={`${m.thingName}-${m.sinceTs}`}>
                          <s.RowName title={m.thingName}>
                            {m.thingName}
                          </s.RowName>
                          <s.RowMeta>
                            {[m.parkName, m.status].filter(Boolean).join(' · ')}
                            {m.sinceTs ? ` · ${fmtSince(m.sinceTs)}` : ''}
                          </s.RowMeta>
                        </s.Row>
                      ))}
                    </s.Section>
                  )}

                  {stats.topOffenders && stats.topOffenders.length > 0 && (
                    <s.Section>
                      <s.SectionLabel>Causas mais frequentes</s.SectionLabel>
                      {stats.topOffenders.map((o) => (
                        <s.Row key={`${o.cause}-${o.thingName}`}>
                          <s.RowName title={o.cause}>{o.cause}</s.RowName>
                          <s.RowMeta>
                            {o.thingName} · {o.count}×
                          </s.RowMeta>
                        </s.Row>
                      ))}
                    </s.Section>
                  )}
                </>
              )}

              {/* Copiloto: conversa livre sobre a operacao */}
              {chat.length > 0 && (
                <s.Section>
                  <s.SectionLabel>Copiloto</s.SectionLabel>
                  {chat.map((m, i) => (
                    <s.ChatMsg
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${m.role}-${i}`}
                      $user={m.role === 'user'}
                    >
                      {m.content}
                    </s.ChatMsg>
                  ))}
                  {asking && <s.ChatMsg $thinking>Consultando dados…</s.ChatMsg>}
                  <div ref={chatEndRef} />
                </s.Section>
              )}
            </>
          )}
        </s.Body>

        {/* Barra de pergunta do copiloto */}
        <s.AskBar>
          <s.AskInput
            type="text"
            placeholder="Pergunte sobre a operação — ex.: qual máquina mais parou essa semana?"
            value={chatInput}
            maxLength={500}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleAsk();
            }}
            disabled={asking}
          />
          <s.AskSend
            type="button"
            onClick={handleAsk}
            disabled={asking || !chatInput.trim()}
            title="Perguntar ao copiloto"
            aria-label="Perguntar"
          >
            {/* lucide:send */}
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="m22 2-7 20-4-9-9-4z" />
              <path d="M22 2 11 13" />
            </svg>
          </s.AskSend>
        </s.AskBar>

        <s.Footer>
          <s.FootNote>
            {data && data.generatedAt
              ? `Gerado ${new Date(data.generatedAt).toLocaleTimeString('pt-BR', {
                  hour: '2-digit',
                  minute: '2-digit',
                })} · janela de ${(data && data.windowHours) || hours}h`
              : ''}
          </s.FootNote>
          <s.Refresh type="button" onClick={() => load(hours)} disabled={loading}>
            Atualizar
          </s.Refresh>
        </s.Footer>
      </s.Panel>
    </s.Backdrop>,
    document.body,
  );
};

export default ShiftSummaryModal;
